import { BareEventTemplate } from '@go-shared/types/eventTemplate';
import { Template } from '@go-shared/types/template';
import React from 'react';
import { BubbleAlert, BubbleButton, BubbleOverlay, BubbleSelect, BubbleTable } from '../BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '../BubbleUI/BubbleTable';
import { FormGenericContainer } from '../FormGenericContainer';
import { Promotable, TransferForm } from './TemplateTransferForm';
import TmplTnsfStatusFooter from './TmplTnsfStatusFooter';

interface TmplTransferEvtPageProps {
  transferForm: TransferForm;
  template: Template;
  isBusy: boolean;
  changeTransferForm: <T extends keyof TransferForm>(key: T, val: TransferForm[T]) => void;
  onGoBack?: () => void;
  onGoNext?: () => void;
  onClose?: () => void;
}

export function TmplTransferEvtPage(props: TmplTransferEvtPageProps) {
  const tableFields: Array<BubbleTableFields<Promotable<BareEventTemplate>>> = [
    { key: 'title' },
    { key: 'access', label: 'Resolve By', behavior: BubbleTableFieldBehavior.Condense },
  ];

  const tableTemplates: Array<BubbleTableTemplate<Promotable<BareEventTemplate>>> = [
    {
      key: 'title',
      render: data => (
        <span className={data.strategy ? '' : 'text-red-600'}>{data.item.title}</span>
      ),
    },
    {
      key: 'access',
      render: (data, index) => (
        <BubbleSelect
          allowUndefined
          value={data.strategy}
          options={['add-property-to-access', 'promote-to-brand']}
          undefinedLabel={'Do not transfer'}
          selectClassName={data.strategy ? '' : 'text-red-600'}
          labelExtractor={t =>
            t === 'add-property-to-access' ? 'Add Property to Access' : 'Promote to Brand'
          }
          onValue={val => {
            const newItems = [...props.transferForm.eventsFix];
            newItems[index].strategy = val;
            props.changeTransferForm('eventsFix', newItems);
          }}
        />
      ),
    },
  ];

  return (
    <div className="tflf-2">
      <FormGenericContainer
        footerClassName="flex-wrap"
        footer={() => (
          <>
            <TmplTnsfStatusFooter {...props} className="text-slate-dark mr-auto" />
            <BubbleButton
              className="ml-auto"
              variant="navy"
              inverse
              onClick={props.onGoBack}
              disabled={props.isBusy}
            >
              Go Back
            </BubbleButton>
            <BubbleOverlay loading={props.isBusy}>
              <BubbleButton variant="navy" onClick={props.onGoNext}>
                Next: Map Venues
              </BubbleButton>
            </BubbleOverlay>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div>
            <h1 className="text-lg text-slate-dark font-semibold">Transfer Template</h1>
            <h2 className="text-2xl text-navy">2. Confirm Events</h2>
          </div>
          <BubbleButton
            disabled={props.isBusy}
            onClick={props.onClose}
            className="ml-auto"
            variant="navy"
            inverse
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        {props.transferForm.eventsFix.length > 0 ? (
          <>
            <BubbleAlert variant="banana" className="mb-3">
              Some events are not accessible by the target property. Please resolve these issues by
              selecting a strategy for each event below.
            </BubbleAlert>
            <BubbleTable
              headVariant="navy"
              striped
              responsive
              fields={tableFields}
              items={props.transferForm.eventsFix}
              templates={tableTemplates}
              keyExtractor={i => i.item._id}
            />
          </>
        ) : (
          <BubbleAlert variant="ocean">
            All events are reachable by the target property. No action is needed on this panel.
          </BubbleAlert>
        )}
      </FormGenericContainer>
    </div>
  );
}
